/**
 * SentryMetaTags Component
 * Dynamically updates Open Graph and Twitter meta tags for sentry.trading domain
 * Removes Mavrk branding and og:image for Sentry domain
 */

import { useEffect } from 'react';
import { useLocation } from 'react-router-dom';

const SentryMetaTags: React.FC = () => {
  const location = useLocation();

  useEffect(() => {
    const hostname = window.location.hostname;
    const isSentryDomain = hostname === 'sentry.trading' || hostname === 'www.sentry.trading';

    if (isSentryDomain) {
      // Helper function to update or create meta tags
      const updateMetaTag = (property: string, content: string, isProperty = true) => {
        const selector = isProperty ? `meta[property="${property}"]` : `meta[name="${property}"]`;
        let meta = document.querySelector(selector) as HTMLMetaElement;
        
        if (!meta) {
          meta = document.createElement('meta');
          if (isProperty) {
            meta.setAttribute('property', property);
          } else {
            meta.setAttribute('name', property);
          }
          document.head.appendChild(meta);
        }
        meta.setAttribute('content', content);
      };

      const updateLinkTag = (rel: string, href: string, sizes?: string) => {
        const selector = `link[rel="${rel}"]${sizes ? `[sizes="${sizes}"]` : ''}`;
        let link = document.querySelector(selector) as HTMLLinkElement;

        if (!link) {
          link = document.createElement('link');
          link.setAttribute('rel', rel);
          if (sizes) {
            link.setAttribute('sizes', sizes);
          }
          document.head.appendChild(link);
        }
        link.setAttribute('href', href);
      };

      const ogImageUrl =
        'https://esjrycmiokijtxnbfyox.supabase.co/storage/v1/object/public/web-images/sentry%20web%20preview%20link1.png';
      const faviconUrl =
        'https://esjrycmiokijtxnbfyox.supabase.co/storage/v1/object/public/web-images/Sentry%20-%20Gold%20-%20Flat.png';

      // Determine page type based on pathname
      const path = location.pathname;
      const isDashboardPage = path === '/dashboard' || path.startsWith('/dashboard/');
      const isDocsPage = path === '/docs' || path.startsWith('/docs/');
      const isTokensPage = path === '/tokens' || path.startsWith('/tokens/');

      // Update title and description based on page
      if (isDashboardPage) {
        document.title = 'Sentry - Dashboard';
        updateMetaTag('title', 'Sentry - Dashboard', false);
        updateMetaTag('description', 'Sentry dashboard and analytics.', false);
        updateMetaTag('og:title', 'Sentry - Dashboard');
        updateMetaTag('og:description', 'Sentry dashboard and analytics.');
        updateMetaTag('twitter:title', 'Sentry - Dashboard');
        updateMetaTag('twitter:description', 'Sentry dashboard and analytics.');
      } else if (isDocsPage) {
        document.title = 'Sentry - Documentation';
        updateMetaTag('title', 'Sentry - Documentation', false);
        updateMetaTag('description', 'Sentry documentation and guides.', false);
        updateMetaTag('og:title', 'Sentry - Documentation');
        updateMetaTag('og:description', 'Sentry documentation and guides.');
        updateMetaTag('twitter:title', 'Sentry - Documentation');
        updateMetaTag('twitter:description', 'Sentry documentation and guides.');
      } else if (isTokensPage) {
        document.title = 'Sentry - Explore Tokens';
        updateMetaTag('title', 'Sentry - Explore Tokens', false);
        updateMetaTag('description', 'Explore tokens on Sentry.', false);
        updateMetaTag('og:title', 'Sentry - Explore Tokens');
        updateMetaTag('og:description', 'Explore tokens on Sentry.');
        updateMetaTag('twitter:title', 'Sentry - Explore Tokens');
        updateMetaTag('twitter:description', 'Explore tokens on Sentry.');
      } else {
        // Landing page
        document.title = 'Sentry';
        updateMetaTag('title', 'Sentry', false);
        updateMetaTag('description', 'Sentry dashboard and analytics.', false);
        updateMetaTag('og:title', 'Sentry');
        updateMetaTag('og:description', 'Sentry dashboard and analytics.');
        updateMetaTag('twitter:title', 'Sentry');
        updateMetaTag('twitter:description', 'Sentry dashboard and analytics.');
      }

      // Update common meta tags
      updateMetaTag('og:type', 'website');
      updateMetaTag('og:site_name', 'Sentry');
      updateMetaTag('og:url', `https://${hostname}${location.pathname}`);
      updateMetaTag('og:image', ogImageUrl);
      updateMetaTag('og:image:width', '1200');
      updateMetaTag('og:image:height', '630');
      updateMetaTag('theme-color', '#b57f09', false);
      updateMetaTag('apple-mobile-web-app-capable', 'yes', false);
      updateMetaTag('apple-mobile-web-app-status-bar-style', 'black-translucent', false);
      updateMetaTag('apple-mobile-web-app-title', 'Sentry', false);
      updateMetaTag('mobile-web-app-capable', 'yes', false);

      // Update Twitter card (with image)
      updateMetaTag('twitter:card', 'summary_large_image');
      updateMetaTag('twitter:url', `https://${hostname}${location.pathname}`);
      updateMetaTag('twitter:image', ogImageUrl);

      // Update favicon + PWA manifest for Sentry domain
      updateLinkTag('icon', faviconUrl);
      updateLinkTag('shortcut icon', faviconUrl);
      updateLinkTag('manifest', '/sentry-manifest.json');
      updateLinkTag('apple-touch-icon', '/Sentry-192x192.png', '192x192');
      updateLinkTag('apple-touch-icon', '/Sentry-512x512.png', '512x512');
    }
  }, [location.pathname]);

  return null; // This component doesn't render anything
};

export default SentryMetaTags;
